import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import MonitoringLayout from "../components/MonitoringLayout";
import apiFetch from "../utils/apiFetch";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

/* ── Конфиги ── */
const STAT_CARDS = [
  { key: "open",                   label: "Жами очиқ",        mod: "open",      link: "/monitoring" },
  { key: "emergency",              label: "🚨 Авариявий",     mod: "emergency", link: "/monitoring?is_emergency=true" },
  { key: "MASTER_REVIEW",          label: "Мастер",            mod: "master",    link: "/monitoring?status=MASTER_REVIEW" },
  { key: "LINE_CHIEF_REVIEW",      label: "Тармоқ бошлиғи",   mod: "chief",     link: "/monitoring?status=LINE_CHIEF_REVIEW" },
  { key: "BRANCH_ENGINEER_REVIEW", label: "Бош муҳандис",      mod: "engineer",  link: "/monitoring?status=BRANCH_ENGINEER_REVIEW" },
  { key: "overdue",                label: "Муддати ўтган",     mod: "overdue",   link: "/monitoring?status=OVERDUE" },
  { key: "CLOSED",                 label: "Ёпилган",           mod: "closed",    link: "/monitoring?status=CLOSED" },
];

const BRANCH_COLS = [
  { key: "open",                   label: "Очиқ",          mod: "open",      status: "" },
  { key: "emergency",              label: "Авария",        mod: "emergency", status: "", emergency: true },
  { key: "MASTER_REVIEW",          label: "Мастер",        mod: "master",    status: "MASTER_REVIEW" },
  { key: "LINE_CHIEF_REVIEW",      label: "Бошлиқ",        mod: "chief",     status: "LINE_CHIEF_REVIEW" },
  { key: "BRANCH_ENGINEER_REVIEW", label: "Муҳандис",      mod: "engineer",  status: "BRANCH_ENGINEER_REVIEW" },
  { key: "overdue",                label: "Муддати ўтган", mod: "overdue",   status: "OVERDUE" },
  { key: "CLOSED",                 label: "Ёпилган",       mod: "closed",    status: "CLOSED" },
];

const COLORS = {
  created: "#5b7ef5",
  closed: "#34d399",
  master: "#fbbf24",
  chief: "#60a5fa",
  engineer: "#a78bfa",
  overdue: "#f87171",
  avg: "#fb923c",
};

/* ── Форматирование ── */
const fmtDate = (d) => {
  if (!d) return "";
  const parts = d.split("-");
  return `${parts[2]}.${parts[1]}`;
};

const fmtHours = (h) => {
  if (!h && h !== 0) return "—";
  if (h < 24) return `${h} соат`;
  const days = Math.round(h / 24);
  return `${days} кун`;
};

/* ── Кастомный тултип для графиков ── */
function ChartTooltip({ active, payload, label, names }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="mon-chart-tooltip">
      <div className="mon-chart-tooltip__label">{fmtDate(label)}</div>
      {payload.map((p, i) => (
        <div key={i} className="mon-chart-tooltip__row">
          <span className="mon-chart-tooltip__dot" style={{ background: p.color }} />
          {names?.[p.dataKey] || p.dataKey}: <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
}

/* ── Компонент ── */
export default function MonitoringStats() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [branches, setBranches] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      apiFetch("/api/incidents/stats").then((r) => { if (!r.ok) throw new Error("Юкланмади"); return r.json(); }),
      apiFetch("/api/incidents/stats/branches").then((r) => { if (!r.ok) throw new Error("Юкланмади"); return r.json(); }),
      apiFetch("/api/incidents/stats/analytics").then((r) => { if (!r.ok) throw new Error("Юкланмади"); return r.json(); }),
    ])
      .then(([s, b, a]) => {
        setStats(s);
        setBranches(b.branches || []);
        setAnalytics(a);
        setLoading(false);
      })
      .catch((e) => {
        if (e?.status === 401) navigate("/login");
        else if (e?.status === 403) navigate("/no-access");
        else setError(e.message || "Хатолик");
        setLoading(false);
      });
  }, [navigate]);

  const getValue = (key) => {
    if (!stats) return 0;
    if (key === "open") return stats.open ?? 0;
    if (key === "overdue") return stats.overdue ?? 0;
    if (key === "emergency") return stats.emergency ?? 0;
    return stats.by_status?.[key] ?? 0;
  };

  const totals = branches.length > 1
    ? BRANCH_COLS.reduce((acc, c) => {
        acc[c.key] = branches.reduce((s, b) => s + (b[c.key] || 0), 0);
        return acc;
      }, {})
    : null;

  return (
    <MonitoringLayout>
      <div className="mon-page-header">
        <div>
          <h1 className="mon-page-title">Статистика</h1>
          <p className="mon-page-sub">Нуқсонлар бўйича умумий кўрсаткичлар</p>
        </div>
      </div>

      {loading && <div className="mon-loading">Юкланмоқда...</div>}
      {error && !loading && <div className="mon-error">{error}</div>}

      {!loading && !error && stats && (
        <>
          {/* ── Карточки ── */}
          <div className="mon-stat-grid">
            {STAT_CARDS.map((card) => (
              <Link key={card.key} to={card.link} className={`mon-stat-card mon-stat-card--${card.mod} mon-stat-card--link`}>
                <div className="mon-stat-card__label">{card.label}</div>
                <div className="mon-stat-card__value">{getValue(card.key)}</div>
              </Link>
            ))}
          </div>

          {/* ── Графики ── */}
          {analytics && (
            <div className="mon-charts-grid">
              {/* График 1: Динамика создания / закрытия */}
              <div className="mon-card mon-chart-card">
                <p className="mon-card__title">Динамика (30 кун)</p>
                <p className="mon-card__subtitle">Яратилган ва ёпилган нуқсонлар</p>
                <div className="mon-chart-wrap">
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={analytics.daily} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gCreated" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.created} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={COLORS.created} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gClosed" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.closed} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={COLORS.closed} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(45,52,84,0.5)" />
                      <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fill: "#8b9ab5", fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                      <YAxis allowDecimals={false} tick={{ fill: "#8b9ab5", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip names={{ created: "Яратилган", closed: "Ёпилган" }} />} />
                      <Legend formatter={(v) => v === "created" ? "Яратилган" : "Ёпилган"} wrapperStyle={{ fontSize: 12 }} />
                      <Area type="monotone" dataKey="created" stroke={COLORS.created} fill="url(#gCreated)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                      <Area type="monotone" dataKey="closed" stroke={COLORS.closed} fill="url(#gClosed)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* График 2: Среднее время закрытия по филиалам — вертикальный */}
              {analytics.avg_close_time?.length > 0 && (() => {
                const maxH = Math.max(...analytics.avg_close_time.map((d) => d.avg_hours), 1);
                const sorted = [...analytics.avg_close_time]
                  .sort((a, b) => b.avg_hours - a.avg_hours)
                  .map((d) => {
                    const ratio = d.avg_hours / maxH;
                    let color = "#22d3ee";
                    if (ratio > 0.75) color = "#ef4444";
                    else if (ratio > 0.5) color = "#f97316";
                    else if (ratio > 0.25) color = "#eab308";
                    return { ...d, _color: color };
                  });
                return (
                  <div className="mon-card mon-chart-card">
                    <p className="mon-card__title">Ўртача ёпилиш вақти</p>
                    <p className="mon-card__subtitle">Филиаллар бўйича (соат)</p>
                    <div className="mon-chart-wrap">
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={sorted}
                          margin={{ top: 24, right: 8, left: -10, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(45,52,84,0.5)" vertical={false} />
                          <XAxis
                            dataKey="branch_name"
                            tick={({ x, y, payload }) => (
                              <text x={x} y={y + 8} textAnchor="end" fill="#e2e8f0" fontSize={11} transform={`rotate(-45, ${x}, ${y + 8})`}>
                                {payload.value}
                              </text>
                            )}
                            axisLine={false}
                            tickLine={false}
                            interval={0}
                          />
                          <YAxis
                            tick={{ fill: "#8b9ab5", fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(v) => `${v}ч`}
                          />
                          <Tooltip
                            cursor={{ fill: "rgba(255,255,255,0.04)" }}
                            content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;
                              const d = payload[0].payload;
                              return (
                                <div className="mon-chart-tooltip">
                                  <div className="mon-chart-tooltip__label">{d.branch_name}</div>
                                  <div className="mon-chart-tooltip__row">Ўртача: <strong>{fmtHours(d.avg_hours)}</strong></div>
                                  <div className="mon-chart-tooltip__row">Ёпилган: <strong>{d.count} та</strong></div>
                                </div>
                              );
                            }}
                          />
                          <Bar
                            dataKey="avg_hours"
                            fill={COLORS.avg}
                            radius={[6, 6, 0, 0]}
                            barSize={36}
                            isAnimationActive={false}
                            label={{ position: "top", fill: "#cbd5e1", fontSize: 11, formatter: (v) => fmtHours(v) }}
                            shape={(props) => {
                              const { x, y, width, height, payload } = props;
                              return <rect x={x} y={y} width={width} height={height} rx={6} ry={6} fill={payload._color} />;
                            }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })()}

              {/* Таблица 3: Топ тармоқлар по нуқсонлар */}
              {analytics.top_networks?.length > 0 && (
                <div className="mon-card">
                  <p className="mon-card__title">Энг кўп нуқсонли тармоқлар</p>
                  <p className="mon-card__subtitle">Топ {analytics.top_networks.length} та тармоқ</p>
                  <div className="mon-branch-table-wrap">
                    <table className="mon-branch-table">
                      <thead>
                        <tr>
                          <th className="mon-branch-table__th-name">#</th>
                          <th className="mon-branch-table__th-name">Тармоқ</th>
                          <th className="mon-branch-table__th-name">Филиал</th>
                          <th className="mon-branch-table__th" style={{ textAlign: "center" }}>Нуқсонлар</th>
                          <th className="mon-branch-table__th" style={{ minWidth: 120 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.top_networks.map((n, i) => {
                          const maxCount = analytics.top_networks[0]?.count || 1;
                          const pct = Math.round((n.count / maxCount) * 100);
                          return (
                            <tr key={n.network_id || i} className="mon-branch-table__row">
                              <td className="mon-branch-table__cell" style={{ color: "#8b9ab5", width: 30 }}>{i + 1}</td>
                              <td className="mon-branch-table__name">{n.network_name}</td>
                              <td className="mon-branch-table__cell" style={{ color: "#8b9ab5" }}>{n.branch_name}</td>
                              <td className="mon-branch-table__cell" style={{ textAlign: "center", fontWeight: 600 }}>{n.count}</td>
                              <td className="mon-branch-table__cell" style={{ paddingRight: 12 }}>
                                <div className="mon-progress-bar">
                                  <div className="mon-progress-bar__fill mon-progress-bar__fill--pink" style={{ width: `${pct}%` }} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Таблица 4: Кўрик натижалари — филиаллар бўйича */}
              {analytics.checkup_stats?.length > 0 && (
                <div className="mon-card">
                  <p className="mon-card__title">Кўрик натижалари</p>
                  <p className="mon-card__subtitle">Филиаллар бўйича кўриклар ва нуқсонлар</p>
                  <div className="mon-branch-table-wrap">
                    <table className="mon-branch-table">
                      <thead>
                        <tr>
                          <th className="mon-branch-table__th-name">Филиал</th>
                          <th className="mon-branch-table__th" style={{ textAlign: "center" }}>Кўриклар</th>
                          <th className="mon-branch-table__th" style={{ textAlign: "center" }}>Нуқсон</th>
                          <th className="mon-branch-table__th" style={{ textAlign: "center" }}>%</th>
                          <th className="mon-branch-table__th" style={{ minWidth: 140 }}>Нуқсон улуши</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.checkup_stats.map((s, i) => {
                          const pct = s.total > 0 ? Math.round((s.with_incidents / s.total) * 100) : 0;
                          return (
                            <tr key={i} className="mon-branch-table__row">
                              <td className="mon-branch-table__name">{s.branch_name}</td>
                              <td className="mon-branch-table__cell" style={{ textAlign: "center" }}>{s.total}</td>
                              <td className="mon-branch-table__cell" style={{ textAlign: "center", fontWeight: 600, color: s.with_incidents > 0 ? "#f87171" : undefined }}>{s.with_incidents}</td>
                              <td className="mon-branch-table__cell" style={{ textAlign: "center", color: "#8b9ab5" }}>{pct}%</td>
                              <td className="mon-branch-table__cell" style={{ paddingRight: 12 }}>
                                <div className="mon-progress-bar">
                                  <div className="mon-progress-bar__fill mon-progress-bar__fill--red" style={{ width: `${pct}%` }} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {analytics.checkup_stats.length > 1 && (() => {
                        const totals = analytics.checkup_stats.reduce((acc, s) => ({ total: acc.total + s.total, incidents: acc.incidents + s.with_incidents }), { total: 0, incidents: 0 });
                        const totalPct = totals.total > 0 ? Math.round((totals.incidents / totals.total) * 100) : 0;
                        return (
                          <tfoot>
                            <tr className="mon-branch-table__totals">
                              <td className="mon-branch-table__name">Жами</td>
                              <td className="mon-branch-table__cell mon-branch-table__cell--total" style={{ textAlign: "center" }}>{totals.total}</td>
                              <td className="mon-branch-table__cell mon-branch-table__cell--total" style={{ textAlign: "center" }}>{totals.incidents}</td>
                              <td className="mon-branch-table__cell mon-branch-table__cell--total" style={{ textAlign: "center" }}>{totalPct}%</td>
                              <td className="mon-branch-table__cell mon-branch-table__cell--total"></td>
                            </tr>
                          </tfoot>
                        );
                      })()}
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Таблица по филиалам ── */}
          {branches.length > 0 && (
            <div className="mon-card" style={{ marginTop: 8 }}>
              <p className="mon-card__title">Филиаллар бўйича</p>
              <div className="mon-branch-table-wrap">
                <table className="mon-branch-table">
                  <thead>
                    <tr>
                      <th className="mon-branch-table__th-name">Филиал</th>
                      {BRANCH_COLS.map((c) => (
                        <th key={c.key} className={`mon-branch-table__th mon-branch-table__th--${c.mod}`}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {branches.map((b) => (
                      <tr key={b.branch_id} className="mon-branch-table__row">
                        <td className="mon-branch-table__name">{b.branch_name}</td>
                        {BRANCH_COLS.map((c) => {
                          const v = b[c.key] || 0;
                          const isOverdue = c.key === "overdue" && v > 0;
                          const isEmergency = c.key === "emergency" && v > 0;
                          const href = c.emergency ? "/monitoring?is_emergency=true" : c.status ? `/monitoring?status=${c.status}` : "/monitoring";
                          return (
                            <td key={c.key} className={"mon-branch-table__cell" + (isOverdue ? " mon-branch-table__cell--overdue" : "") + (isEmergency ? " mon-branch-table__cell--emergency" : "") + (v === 0 ? " mon-branch-table__cell--zero" : "")}>
                              {v > 0 ? <Link to={href} className="mon-branch-table__link">{v}</Link> : v}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                  {totals && (
                    <tfoot>
                      <tr className="mon-branch-table__totals">
                        <td className="mon-branch-table__name">Жами</td>
                        {BRANCH_COLS.map((c) => {
                          const v = totals[c.key] || 0;
                          const isOverdue = c.key === "overdue" && v > 0;
                          return (
                            <td key={c.key} className={"mon-branch-table__cell mon-branch-table__cell--total" + (isOverdue ? " mon-branch-table__cell--overdue" : "")}>
                              {v}
                            </td>
                          );
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </MonitoringLayout>
  );
}
